import { type Moment, moment } from "@msuite/utils/date";
import { Holidays } from "@msuite/utils/holidays";
import { useCallback, useMemo, useState } from "react";

export type AbsencesCalendarController = ReturnType<typeof useAbsencesCalendar>;

type AbsenceCalendarMode = "week" | "month" | "quarter" | "year";

export interface AbsencesCalendarCell {
	cellMoment: Moment;
	isInRange: boolean;
	isWeekend: boolean;
	isHoliday: boolean;
	isToday: boolean;
}

const FIRST_COLUMN_WIDTH = "300px";
const DATE_COLUMN_WIDTH = "32px";

function modeToSegmentType(mode: AbsenceCalendarMode): "day" | "week" {
	switch (mode) {
		case "month":
		case "week":
			return "day";
		case "quarter":
		case "year":
			return "week";
	}
}

export function useAbsencesCalendar() {
	/** State */
	const [_current, setCurrent] = useState<Moment>(moment());
	const [mode, setMode] = useState<AbsenceCalendarMode>("month");

	/** Memoized */
	const current = useMemo(() => _current.clone().startOf(mode), [_current, mode]);

	const segmentType = useMemo(() => modeToSegmentType(mode), [mode]);

	const columns = useMemo(() => {
		switch (mode) {
			case "week":
				return 7;
			case "month":
				return 31;
			case "quarter":
				return Math.abs(
					current.clone().startOf("quarter").diff(current.clone().endOf("quarter"), "weeks"),
				);
			case "year":
				return current.weeksInYear();
		}
	}, [current, mode]);

	const actualColumns = useMemo(
		() => (mode === "month" ? current.daysInMonth() : columns),
		[current, mode, columns],
	);

	const currentRange = useMemo(
		() => ({ start: current.clone().startOf(mode), end: current.clone().endOf(mode) }),
		[current, mode],
	);

	const currentString = useMemo(() => {
		switch (mode) {
			case "week":
				return current.format("[Kalenderwoche] WW / YYYY");
			case "month":
				return current.format("MMMM YYYY");
			case "quarter":
				return current.format("Q YYYY");
			case "year":
				return current.format("YYYY");
		}
	}, [current, mode]);

	const cells = useMemo<AbsencesCalendarCell[]>(() => {
		const today = moment();
		const isDay = segmentType === "day";
		const result: AbsencesCalendarCell[] = new Array(columns);
		for (let i = 0; i < columns; i++) {
			const cellMoment = current.clone().add(i, segmentType);
			const isInRange = cellMoment.isBetween(currentRange.start, currentRange.end, mode, "[]");
			const isWeekend = isDay && cellMoment.isoWeekday() >= 6;
			const isHoliday = isDay && Holidays.isHoliday(cellMoment.toDate(), "BE");
			const isToday = isDay && cellMoment.isSame(today, "day");
			result[i] = { cellMoment, isInRange, isWeekend, isHoliday, isToday };
		}
		return result;
	}, [columns, current, mode, segmentType, currentRange]);

	/** Functions */
	const onForward = useCallback(() => {
		setCurrent((prev) => prev.clone().add(1, mode));
	}, [mode]);

	const onBack = useCallback(() => {
		setCurrent((prev) => prev.clone().subtract(1, mode));
	}, [mode]);

	const getHeaderMoment = useCallback(
		(index: number) => current.clone().add(index, segmentType),
		[current, segmentType],
	);

	const getIsInRange = useCallback(
		(m: Moment) => m.isBetween(currentRange.start, currentRange.end, mode, "[]"),
		[currentRange, mode],
	);

	const getColumns = useCallback(() => columns, [columns]);
	const getActualColumns = useCallback(() => actualColumns, [actualColumns]);
	const getCurrentString = useCallback(() => currentString, [currentString]);
	const getCurrentRange = useCallback(() => currentRange, [currentRange]);
	const getModeSegmentType = useCallback(() => segmentType, [segmentType]);

	const constants = useMemo(
		() => ({
			FIRST_COLUMN_WIDTH,
			DATE_COLUMN_WIDTH,
			gridTemplateColumns: `repeat(${columns}, ${DATE_COLUMN_WIDTH})`,
		}),
		[columns],
	);

	return useMemo(
		() => ({
			current,
			mode,
			columns,
			actualColumns,
			segmentType,
			cells,
			onForward,
			onBack,
			setCurrent,
			setMode,
			getColumns,
			getActualColumns,
			getCurrentString,
			getCurrentRange,
			getIsInRange,
			getHeaderMoment,
			getModeSegmentType,
			constants,
		}),
		[
			current,
			mode,
			columns,
			actualColumns,
			segmentType,
			cells,
			onForward,
			onBack,
			getColumns,
			getActualColumns,
			getCurrentString,
			getCurrentRange,
			getIsInRange,
			getHeaderMoment,
			getModeSegmentType,
			constants,
		],
	);
}
